import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import './TakeTest.css';
import './Challenge.css';
import TestResultScreen from '../components/practice/TestResultScreen';
import ComboIndicator from '../components/player/ComboIndicator';
import QuestionImage from '../components/ui/QuestionImage';
import QuestionText from '../components/ui/QuestionText';
import { usePopup } from '../context/PopupContext';

const FLOW_LABELS = {
  quick: 'Quick Practice',
  category: 'Category Practice',
  difficulty: 'Difficulty Practice',
  custom: 'Custom Practice',
};

const MockTestTake = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { showConfirm } = usePopup();

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  // Distinct from `result.error` (a server-processed failure) — this is for
  // when the submit request never reached the server at all (offline, DNS,
  // aborted). Found in QA: the old code treated both cases identically,
  // showing a result screen that claimed "it's been recorded" and a
  // fabricated 0/0/0 breakdown even when nothing had been sent. A network
  // failure is safely retryable (nothing was consumed server-side), so this
  // keeps the student on the exam view with their answers intact instead.
  const [submitError, setSubmitError] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [instantFeedback, setInstantFeedback] = useState(true);
  const [combo, setCombo] = useState(0);
  const [playerDelta, setPlayerDelta] = useState(null); // { xpGained, fromLevel, toLevel } — only ever real, diffed values

  const submittedRef = useRef(false);
  const startTime = useRef(Date.now());
  const beforeProgressRef = useRef(null);

  const questions = React.useMemo(() => state?.questions || [], [state]);
  const session_id = state?.session_id;
  const flowMode = state?.flowMode || 'quick';

  const optText = (opt) => (opt && typeof opt === 'object' ? (opt.option_text ?? opt.text ?? '') : opt);

  // The answer key is NOT sent with the questions any more (it used to ship in
  // the /mock/start payload, readable in the network tab before answering).
  // Instant feedback asks the server once per question, after the student has
  // committed an answer: feedback[questionId] = { is_correct, correct_option
  // (1-based), explanation }. Combo stays purely cosmetic and is derived from
  // the same reveal.
  const [feedback, setFeedback] = useState({});
  const checkAnswer = useCallback(async (qid, optNum, isFirst) => {
    if (!session_id) return;
    try {
      const r = await API.post(`/mock/session/${session_id}/check`, { question_id: qid, selected_option: optNum });
      setFeedback(p => ({ ...p, [qid]: r.data }));
      if (isFirst) setCombo(c => (r.data.is_correct ? c + 1 : 0));
    } catch (err) {
      // Feedback is a nicety: a failed lookup never blocks answering.
    }
  }, [session_id]);

  useEffect(() => {
    if (!state) { navigate('/student/mock'); return; }
    setTimeLeft(Math.max(questions.length * 90, 60));
    // Best-effort "before" snapshot of real Level/XP for the honest post-
    // submit delta below — never blocks the question screen if it fails.
    API.get('/portal/player-profile').then(res => { beforeProgressRef.current = res.data; }).catch(() => {});
  }, []); // eslint-disable-line

  // ── Timer ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (timeLeft <= 0 || submitted) return;
    const iv = setInterval(() => {
      setTimeLeft(p => {
        if (p <= 1) {
          clearInterval(iv);
          if (!submittedRef.current) {
            submittedRef.current = true;
            setTimeout(() => handleSubmit(), 0);
          }
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [timeLeft, submitted]); // eslint-disable-line

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    // submittedRef is the synchronous guard; `submitted` state lags a render
    // behind, so checking both with AND let a second call slip through and
    // double-POST before the state update flushed.
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitted(true);

    const timeTaken = Math.floor((Date.now() - startTime.current) / 1000);
    const answersArr = questions.map(q => ({
      question_id: q.id,
      selected_option: answers[q.id] !== undefined ? answers[q.id] : null,
      time_spent: 0,
    }));

    setSubmitError('');
    try {
      const res = await API.post('/mock/submit', {
        session_id,
        answers: answersArr,
        time_taken_seconds: timeTaken,
      });
      setResult(res.data.result);
      // Real before/after diff of the same computed Level/XP shown on Home —
      // never a fabricated event, and silently skipped if either read fails.
      API.get('/portal/player-profile').then(after => {
        const before = beforeProgressRef.current;
        if (!before) return;
        setPlayerDelta({
          xpGained: Math.max(0, after.data.xp - before.xp),
          fromLevel: before.level,
          toLevel: after.data.level,
        });
      }).catch(() => {});
    } catch (err) {
      console.error(err);
      if (!err.response) {
        // No HTTP response at all reached us — the request never made it to
        // the server (offline/DNS/aborted), so nothing was recorded.
        // Reset the double-submit guard so the student can safely retry.
        submittedRef.current = false;
        setSubmitted(false);
        setSubmitError("Couldn't reach the server. Check your connection and try again — your answers are still here.");
        return;
      }
      setResult({ error: true, correct: 0, wrong: 0, total: questions.length, percentage: '0' });
    }
  }, [answers, questions, session_id]);

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ── No state guard ───────────────────────────────────────────────────────
  if (!state || questions.length === 0) return (
    <div className="tt-loading">
      No questions loaded.
      <button className="mt-back-btn" onClick={() => navigate('/student/mock')}>
        Go Back
      </button>
    </div>
  );

  // ── Result screen ─────────────────────────────────────────────────────────
  if (result) {
    return (
      <TestResultScreen
        result={result}
        variant="practice"
        contextLabel={`${FLOW_LABELS[flowMode] || 'Practice'} — Completed`}
        onPrimaryAction={() => navigate('/student/analytics', { state: { justCompleted: true } })}
        onSecondaryAction={() => navigate('/student/mock')}
        secondaryLabel="Practice Again"
        onReviewAction={session_id ? () => navigate(`/student/mock/review/${session_id}`) : null}
        playerDelta={playerDelta}
      />
    );
  }

  // ── Test UI ───────────────────────────────────────────────────────────────
  const q = questions[current];
  const answered = Object.keys(answers).length;
  const fb = feedback[q.id];
  // audit ST-03: this used to default to `0` while `fb` was still loading (the check is an async
  // POST fired on selection). Since option numbers are 1-based, `correctIdx === optNum` was then
  // false for EVERY option including the one actually picked, so a correct answer briefly rendered
  // the 'wrong' (red) class until the response landed and flipped it to 'correct' (green). `null`
  // instead of `0` means "not yet known" is its own falsy-but-distinct state, and every correct/wrong
  // class below is additionally gated on `fb` itself, not just a numeric comparison against it.
  const correctIdx = fb ? fb.correct_option : null;

  return (
    <div className="tt-wrapper mt-challenge">

      {/* Top bar */}
      <div className="tt-topbar">
        <div className="tt-topbar-left">
          <div className="tt-test-name">{FLOW_LABELS[flowMode]}</div>
          <div className="tt-session">
            {questions.length} questions · Practice Mode
            <span className="mt-challenge-badge"><Icon name="bolt" size={14} /> No pressure — practice only</span>
          </div>
        </div>
        <div className="tt-topbar-center">
          <div className={`tt-timer ${timeLeft < 120 ? 'danger' : ''}`}>
            <Icon name="timer" size={18} /> {formatTime(timeLeft)}
          </div>
        </div>
        <div className="tt-topbar-right">
          <span className="tt-feedback-label">Instant Feedback</span>
          <button
            type="button"
            role="switch"
            aria-checked={instantFeedback}
            aria-label="Instant Feedback"
            className={`tt-toggle ${instantFeedback ? 'on' : ''}`}
            onClick={() => setInstantFeedback(p => !p)}
          >
            <span className="tt-toggle-thumb" />
          </button>
        </div>

        {/* Progress runs along the bottom edge of the bar, so it stays attached however tall the bar wraps. */}
        <div className="tt-progress-bar">
          <div className="tt-progress-fill"
            style={{ width: `${(answered / questions.length) * 100}%` }} />
        </div>
      </div>

      {combo >= 2 && (
        <div className="mt-combo-row">
          <ComboIndicator combo={combo} />
        </div>
      )}

      {submitError && <div className="tt-submit-error-banner">{submitError}</div>}

      <div className="tt-body">

        {/* Question area */}
        <div className="tt-question-area">
          <div className="tt-q-header">
            <span className="tt-q-num">QUESTION {current + 1} OF {questions.length}</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {q.topic_name && (
                <span className="mt-diff-pill mt-diff-pill--topic">{q.topic_name}</span>
              )}
              <span className={`mt-diff-pill mt-diff-pill--${q.difficulty || 'medium'}`}>
                {q.difficulty}
              </span>
            </div>
          </div>

          <div className="tt-q-text"><QuestionText text={q.question_text} /></div>
          <QuestionImage path={q.image_path} />

          <div className="tt-options">
            {(q.options || []).map((opt, i) => {
              const optNum = i + 1;
              const selected = answers[q.id] === optNum;
              const isCorrect = correctIdx === optNum;
              const hasAnswered = answers[q.id] !== undefined;

              let cls = 'tt-option';
              if (selected) cls += ' selected';
              // Gated on `fb` itself (not just isCorrect) so neither class can ever apply while the
              // async correctness check is still in flight — see the ST-03 note on `correctIdx` above.
              if (instantFeedback && selected && fb && isCorrect) cls += ' correct';
              if (instantFeedback && selected && fb && !isCorrect) cls += ' wrong';
              if (instantFeedback && !selected && fb && isCorrect && hasAnswered) cls += ' show-correct';
              // audit ST-02: once a question has an answer, it's locked — clicking a different
              // option (even before submitting the whole session) must not overwrite it. `.locked`
              // also suppresses the hover affordance on the now-unclickable options below.
              if (hasAnswered) cls += ' locked';

              return (
                <div key={i} className={cls}
                  onClick={() => {
                    if (!submitted && !hasAnswered) {
                      setAnswers(p => ({ ...p, [q.id]: optNum }));
                      setShowExplanation(false);
                      if (instantFeedback) checkAnswer(q.id, optNum, true);
                    }
                  }}
                >
                  <span className="tt-opt-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="tt-opt-text">{optText(opt)}</span>
                  {/* Show correct/wrong icons only once feedback has actually loaded (ST-03) — never a
                      placeholder/false icon while the check is still in flight. */}
                  {instantFeedback && selected && fb && (
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', color: isCorrect ? 'var(--z-success)' : 'var(--z-danger)' }}>
                      <Icon name={isCorrect ? 'success' : 'error'} size={20} title={isCorrect ? 'Correct' : 'Incorrect'} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {fb?.explanation && (
            <button type="button" className="tt-explanation-toggle" aria-expanded={showExplanation}
              onClick={() => setShowExplanation(p => !p)}>
              <span><Icon name="info" size={16} /> View Step-by-Step Explanation</span>
              <Icon name={showExplanation ? 'chevron-up' : 'chevron-down'} size={16} />
            </button>
          )}
          {showExplanation && fb?.explanation && (
            <div className="tt-explanation">{fb.explanation}</div>
          )}

          {/* Auto-show explanation if wrong answer and instant feedback on */}
          {instantFeedback &&
            answers[q.id] !== undefined &&
            answers[q.id] !== correctIdx &&
            fb?.explanation &&
            !showExplanation && (
              <div className="mt-hint-banner">
                <Icon name="tip" size={16} /> Not this time — click "View Explanation" to understand the answer
              </div>
            )}

          <div className="tt-bottom-nav">
            <button className="tt-btn-prev"
              disabled={current === 0}
              onClick={() => { setCurrent(p => p - 1); setShowExplanation(false); }}>
              <Icon name="chevron-left" size={16} /> Previous
            </button>
            <button className="tt-btn-mark"
              onClick={() => setMarked(p => {
                const n = new Set(p);
                n.has(current) ? n.delete(current) : n.add(current);
                return n;
              })}>
              <Icon name="flag" size={16} /> {marked.has(current) ? 'Marked' : 'Mark for Review'}
            </button>
            {current < questions.length - 1 ? (
              <button className="tt-btn-next"
                onClick={() => { setCurrent(p => p + 1); setShowExplanation(false); }}>
                Next <Icon name="chevron-right" size={16} />
              </button>
            ) : (
              <button className="tt-btn-submit"
                onClick={async () => {
                  if (await showConfirm('Finish Session', `Answered ${answered}/${questions.length} questions. Finish this practice session?`))
                    handleSubmit();
                }}>
                Finish Session
              </button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="tt-sidebar">
          <div className="tt-nav-header">
            Navigator
            <span className="tt-nav-answered">{answered}/{questions.length}</span>
          </div>

          <div className="tt-nav-grid">
            {questions.map((qn, idx) => {
              const sel = answers[qn.id];
              let status = 'unvisited';
              if (marked.has(idx)) status = 'marked';
              else if (sel !== undefined) {
                if (instantFeedback && feedback[qn.id]) {
                  status = sel === feedback[qn.id].correct_option ? 'answered correct' : 'answered wrong';
                } else {
                  status = 'answered';
                }
              }
              return (
                <button key={idx}
                  className={`tt-nav-btn ${status} ${idx === current ? 'current' : ''}`}
                  onClick={() => { setCurrent(idx); setShowExplanation(false); }}>
                  {idx + 1}
                </button>
              );
            })}
          </div>

          <div className="tt-nav-legend">
            <div className="tt-leg"><span className="tt-leg-dot answered" />Answered</div>
            <div className="tt-leg"><span className="tt-leg-dot marked" />Marked</div>
            <div className="tt-leg"><span className="tt-leg-dot unvisited" />Unvisited</div>
            {instantFeedback && (
              <>
                <div className="tt-leg"><span className="tt-leg-dot answered correct" style={{ background: 'var(--z-success)' }} />Correct</div>
                <div className="tt-leg"><span className="tt-leg-dot answered wrong" style={{ background: 'var(--z-danger)' }} />Wrong</div>
              </>
            )}
          </div>

          <button className="tt-finish-btn"
            onClick={async () => {
              if (await showConfirm('Finish Session', `Answered ${answered}/${questions.length} questions. Finish this practice session?`))
                handleSubmit();
            }}>
            Finish Session
          </button>

          <button className="tt-pause-btn" onClick={async () => {
            if (await showConfirm('Exit Practice', 'Your progress in this session will be lost. Exit anyway?'))
              navigate('/student/mock');
          }}>
            Exit Practice
          </button>

          {/* Practice mode info */}
          <div className="mt-mode-info">
            <strong><Icon name="bolt" size={14} /> Practice Mode:</strong><br />
            · No proctoring restrictions<br />
            · Instant feedback available<br />
            · Practice only — does not affect analytics
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTestTake;
